export type QRCodeType = 
  | 'p2p_transfer'      // โอนเงิน P2P
  | 'payment_gateway'   // ชำระเงินร้านค้า
  | 'receipt'           // ใบเสร็จ
  | 'deposit_qr'        // QR ฝากเงิน (ธนาคาร)
  | 'unknown';          // ไม่รู้จัก

export interface QRDetectionResult {
  type: QRCodeType;
  data: any;
  isValid: boolean;
  errorMessage?: string;
  suggestedRoute?: string;
}

interface P2PQRData {
  receiver_id: string;
  wallet_id?: string;
  preset_amount?: number;
  currency?: string;
  name?: string;
  user_code?: string;
  ts: number;
}

// New format for moneyplus app QR (legacy)
interface MoneyPlusQRData {
  type: 'p2p_transfer';
  app: 'moneyplus';
  data: {
    walletId: string;
    userId: string;
    name: string;
    currency: string;
    userCode?: string;
    presetAmount?: number;
  };
}

// Compact QR format v2 (ลดขนาดเพื่อสแกนง่าย)
interface CompactQRData {
  t: 'p2p';           // type
  c: string;          // userCode
  w: string;          // walletId prefix (8 chars)
  a?: number;         // amount
  n?: string;         // name
  y?: string;         // currency
  v: number;          // version
}

interface PaymentGatewayData {
  payment_id: string;
  amount: number;
  currency: string;
  merchant_id: string;
  merchant_name?: string;
  order_id?: string;
  expires_at: string;
  type: string;
}

// Validate legacy P2P QR format
function validateP2PQR(data: any): boolean {
  return !!(
    data.receiver_id &&
    typeof data.receiver_id === 'string' &&
    data.ts &&
    typeof data.ts === 'number'
  );
}

// Validate new moneyplus P2P QR format (legacy)
function validateMoneyPlusQR(data: any): boolean {
  return !!(
    data.type === 'p2p_transfer' &&
    data.app === 'moneyplus' &&
    data.data &&
    data.data.userId &&
    data.data.walletId
  );
}

// Validate compact QR format v2
function validateCompactQR(data: any): boolean {
  return !!(
    data.t === 'p2p' &&
    data.c &&             // userCode required
    data.v >= 2           // version 2+
  );
}

function validatePaymentQR(data: any): boolean {
  return !!(
    data.payment_id &&
    data.amount &&
    data.merchant_id &&
    data.expires_at &&
    data.type === 'payment_gateway'
  );
}

export function detectAndValidateQR(rawData: string): QRDetectionResult {
  // 1. Try to parse as JSON
  try {
    const jsonData = JSON.parse(rawData);
    
    // Check if it's the compact QR format v2 (ใหม่ - สแกนง่าย)
    if (validateCompactQR(jsonData)) {
      const compactData = jsonData as CompactQRData;
      const convertedData: P2PQRData = {
        receiver_id: '',                        // ไม่มีใน compact format, ใช้ userCode lookup แทน
        wallet_id: compactData.w,               // wallet prefix
        preset_amount: compactData.a || undefined,
        currency: compactData.y,
        name: compactData.n,
        user_code: compactData.c,               // userCode เป็น key หลัก
        ts: Date.now()
      };
      return {
        type: 'p2p_transfer',
        data: convertedData,
        isValid: true,
        suggestedRoute: '/p2p-transfer'
      };
    }
    
    // Check if it's the legacy moneyplus P2P QR format
    if (validateMoneyPlusQR(jsonData)) {
      // Convert new format to standard P2PQRData for compatibility
      // Use timestamp from QR if available, otherwise generate new one
      const convertedData: P2PQRData = {
        receiver_id: jsonData.data.userId,
        wallet_id: jsonData.data.walletId,
        preset_amount: jsonData.data.presetAmount,
        currency: jsonData.data.currency,
        name: jsonData.data.name,
        user_code: jsonData.data.userCode,
        ts: jsonData.data.ts || Date.now()
      };
      return {
        type: 'p2p_transfer',
        data: convertedData,
        isValid: true,
        suggestedRoute: '/p2p-transfer'
      };
    }
    
    // Check if it's a legacy P2P Transfer QR
    if (validateP2PQR(jsonData)) {
      return {
        type: 'p2p_transfer',
        data: jsonData as P2PQRData,
        isValid: true,
        suggestedRoute: '/p2p-transfer'
      };
    }
    
    // Check if it's a Payment Gateway QR
    if (validatePaymentQR(jsonData)) {
      return {
        type: 'payment_gateway',
        data: jsonData as PaymentGatewayData,
        isValid: true,
        suggestedRoute: `/pay/${jsonData.payment_id}`
      };
    }
  } catch {
    // Not JSON, continue to URL checks
  }
  
  // 2. Check if it's a URL
  try {
    const url = new URL(rawData);
    
    // Receipt QR: /receipt/[id] or /receipt-view/[id]
    if (url.pathname.match(/^\/(receipt|receipt-view)\/[a-f0-9\-]{36}/)) {
      return {
        type: 'receipt',
        data: { url: rawData },
        isValid: true,
        suggestedRoute: rawData
      };
    }
    
    // Payment Gateway URL (including Edge Function URLs)
    // Whitelist เฉพาะ function/เส้นทางที่ต้องการจริง
    const isGatewayFn = 
      url.pathname.includes('/functions/v1/merchant-payment-gateway') ||
      url.pathname.includes('/functions/v1/merchant-payment');

    const isAppPay = 
      url.pathname.includes('/pay/') ||
      url.pathname.includes('/merchant-payment/');
    
    const isSupabaseEdge = url.hostname.endsWith('supabase.co') && 
                           url.pathname.includes('/functions/v1/');

    if (isAppPay || isGatewayFn) {
      
      // Extract payment_id from URL path
      const pathParts = url.pathname.split('/').filter(Boolean);
      
      // หา payment_id จากหลาย pattern
      let paymentId: string | null = null;
      
      // Pattern 1: /pay/{id}
      const payIndex = pathParts.findIndex(p => p === 'pay');
      if (payIndex !== -1 && pathParts[payIndex + 1]) {
        paymentId = pathParts[payIndex + 1];
      }
      
      // Pattern 2: /merchant-payment-gateway/[payment/]{id}
      if (!paymentId) {
        const gatewayIndex = pathParts.findIndex(p => p === 'merchant-payment-gateway');

        if (gatewayIndex !== -1) {
          // รูปแบบ: .../merchant-payment-gateway/payment/<id>
          if (pathParts[gatewayIndex + 1] === 'payment' && pathParts[gatewayIndex + 2]) {
            paymentId = pathParts[gatewayIndex + 2];
          }
          // รูปแบบ: .../merchant-payment-gateway/<id> (id ตรงๆ)
          else if (pathParts[gatewayIndex + 1] && pathParts[gatewayIndex + 1] !== 'payment') {
            paymentId = pathParts[gatewayIndex + 1];
          }
        }
      }
      
      // Pattern 3: query params
      if (!paymentId) {
        paymentId = url.searchParams.get('payment_id') || url.searchParams.get('id');
      }
      
      return {
        type: 'payment_gateway',
        data: { 
          url: rawData,
          payment_id: paymentId,
          isExternalUrl: isSupabaseEdge
        },
        isValid: true,
        suggestedRoute: paymentId ? `/pay/${paymentId}` : '/qr'  // ✅ ห้ามเป็น rawData
      };
    }
    
    // Deposit QR: bank-related URLs
    if (url.pathname.includes('/deposit') || url.pathname.includes('/bank-qr')) {
      return {
        type: 'deposit_qr',
        data: { url: rawData },
        isValid: true,
        suggestedRoute: '/deposit'
      };
    }
  } catch {
    // Not a valid URL
  }
  
  // 3. Check if it's a simple bank QR code format (PromptPay, etc.)
  if (rawData.length >= 10 && /^\d+$/.test(rawData)) {
    return {
      type: 'deposit_qr',
      data: { promptPayId: rawData },
      isValid: true,
      suggestedRoute: '/deposit'
    };
  }
  
  // 4. Unknown type
  return {
    type: 'unknown',
    data: null,
    isValid: false,
    errorMessage: 'QR Code ไม่รู้จักหรือไม่รองรับ'
  };
}

export function getQRTypeName(type: QRCodeType, locale: 'th' | 'en' | 'lo' = 'th'): string {
  const names: Record<string, Record<QRCodeType, string>> = {
    th: {
      'p2p_transfer': '🤝 QR Code โอนเงิน P2P',
      'payment_gateway': '💳 QR Code ชำระเงินร้านค้า',
      'receipt': '🧾 QR Code ใบเสร็จ',
      'deposit_qr': '🏦 QR Code ฝากเงินธนาคาร',
      'unknown': '❓ QR Code ไม่รู้จัก'
    },
    en: {
      'p2p_transfer': '🤝 P2P Transfer QR Code',
      'payment_gateway': '💳 Payment Gateway QR Code',
      'receipt': '🧾 Receipt QR Code',
      'deposit_qr': '🏦 Bank Deposit QR Code',
      'unknown': '❓ Unknown QR Code'
    },
    lo: {
      'p2p_transfer': '🤝 QR Code ໂອນເງິນ P2P',
      'payment_gateway': '💳 QR Code ຊຳລະເງິນຮ້ານຄ້າ',
      'receipt': '🧾 QR Code ໃບຮັບເງິນ',
      'deposit_qr': '🏦 QR Code ຝາກເງິນທະນາຄານ',
      'unknown': '❓ QR Code ບໍ່ຮູ້ຈັກ'
    }
  };
  
  return names[locale][type];
}
